/**
 * StemPlayer — N-stem aligned playback over a single AudioContext.
 *
 * Architecture
 * - One `AudioContext` per app, owned outside.
 * - One per-stem chain: `AudioBufferSourceNode -> GainNode (mute/solo/vol) -> masterBus`.
 * - One master bus: `GainNode -> AudioContext.destination`.
 * - Sample-accurate alignment guaranteed by starting all sources from the same
 *   `audioContext.currentTime` snapshot and letting the audio thread drive the
 *   timing — never `setTimeout`, never `Date.now`.
 *
 * What this class deliberately does NOT do
 * - Pitch shift / tempo stretch DSP itself — that lives in the optional
 *   `MasterStretchAdapter` (Signalsmith Stretch worklet). Without that
 *   adapter, pitch/rate are coupled (chipmunk effect on rate change) and
 *   `pitchSemitones` is a stub stored only for UI display.
 * - Decoding — see `stemLoader.ts`.
 *
 * The state machine is driven by control-thread method calls; the audio thread
 * remains glitch-free because every transition is anchored to a future
 * `audioContext.currentTime`.
 */

import { invariant } from "@repo/lib/invariant";
import type { MasterStretchAdapter } from "./masterStretch";
import type { LoadedStem, PlayerSnapshot, StemMixSettings, StemRole } from "./types";

interface StemChain {
  readonly role: StemRole;
  readonly buffer: AudioBuffer;
  source: AudioBufferSourceNode | null;
  readonly gain: GainNode;
  /**
   * Tap point for VU metering. Sits in parallel with the master path so the
   * analyser sees the post-gain (post-mute, post-solo) signal but does not
   * itself feed the mix. Lazy-created on first level read so loading a song
   * with no UI metering pays nothing.
   */
  analyser: AnalyserNode | null;
  /** Reusable Float32 sample buffer for getFloatTimeDomainData — never re-allocated. */
  analyserBuffer: Float32Array<ArrayBuffer> | null;
  settings: StemMixSettings;
}

export interface StemPlayerOptions {
  readonly ctx: AudioContext;
  /** Cross-fade time in seconds when changing volume/mute. Default 0.01s. */
  readonly volumeRampSec?: number;
  /** Master volume in 0..1 linear. Default 1. */
  readonly initialMasterVolume?: number;
  /**
   * Optional Signalsmith Stretch adapter for decoupled pitch / rate. When
   * provided, the master bus is routed through `stretch.node` and pitch shifts
   * happen *post*-rate-change on the audio thread. When omitted, pitch/rate
   * are coupled (chipmunk on rate change) — useful for tests + capability
   * detection.
   */
  readonly masterStretch?: MasterStretchAdapter;
}

export type StemPlayerListener = (snap: PlayerSnapshot) => void;

const DEFAULT_RAMP_SEC = 0.01;

export class StemPlayer {
  private readonly ctx: AudioContext;
  private readonly volumeRampSec: number;
  private readonly masterGain: GainNode;
  private readonly meterSink: GainNode;
  private readonly stretch: MasterStretchAdapter | undefined;
  private readonly stems = new Map<StemRole, StemChain>();
  private readonly listeners = new Set<StemPlayerListener>();

  private playing = false;
  /** Audio-thread time at which playback started (sec). */
  private startCtxTime = 0;
  /** Buffer offset (sec) at which playback started. */
  private startBufferOffsetSec = 0;
  /** Pause position (sec). */
  private pausePositionSec = 0;
  private duration = 0;
  private playbackRate = 1;
  private pitchSemitones = 0;
  private state: PlayerSnapshot["state"] = "idle";

  constructor(opts: StemPlayerOptions) {
    this.ctx = opts.ctx;
    this.volumeRampSec = opts.volumeRampSec ?? DEFAULT_RAMP_SEC;
    this.stretch = opts.masterStretch;
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = opts.initialMasterVolume ?? 1;
    this.meterSink = this.ctx.createGain();
    this.meterSink.gain.value = 0;
    this.meterSink.connect(this.ctx.destination);
    if (this.stretch) {
      // master path: stems → masterGain → stretch.node → destination.
      this.masterGain.connect(this.stretch.node);
      this.stretch.node.connect(this.ctx.destination);
      this.stretch.apply({ pitchSemitones: 0, playbackRate: 1 });
    } else {
      this.masterGain.connect(this.ctx.destination);
    }
  }

  /** The underlying AudioContext. Read-only access; use only for lifecycle. */
  get audioContext(): AudioContext {
    return this.ctx;
  }

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────────

  dispose(): void {
    this.stop();
    for (const chain of this.stems.values()) {
      chain.gain.disconnect();
    }
    this.stems.clear();
    this.masterGain.disconnect();
    this.meterSink.disconnect();
    if (this.stretch) {
      this.stretch.dispose();
    }
    this.listeners.clear();
  }

  // ──────────────────────────────────────────────────────────────────
  // Stem management
  // ──────────────────────────────────────────────────────────────────

  /**
   * Replace the current stem set. Stops playback. After this call the player
   * is in `ready` state and the duration reflects the longest stem.
   */
  setStems(stems: readonly LoadedStem[]): void {
    invariant(stems.length > 0, "setStems requires at least one stem");
    this.stop();

    for (const chain of this.stems.values()) {
      chain.gain.disconnect();
    }
    this.stems.clear();

    let maxDuration = 0;
    for (const stem of stems) {
      const gain = this.ctx.createGain();
      gain.gain.value = 1;
      gain.connect(this.masterGain);
      this.stems.set(stem.role, {
        role: stem.role,
        buffer: stem.buffer,
        source: null,
        gain,
        analyser: null,
        analyserBuffer: null,
        settings: { volume: 1, muted: false, solo: false },
      });
      if (stem.buffer.duration > maxDuration) maxDuration = stem.buffer.duration;
    }
    this.duration = maxDuration;
    this.pausePositionSec = 0;
    this.setState("ready");
  }

  getStemRoles(): StemRole[] {
    return [...this.stems.keys()];
  }

  /**
   * Snapshot the currently loaded stems (role + buffer). Returned objects share
   * the live `AudioBuffer`s; do not mutate them.
   */
  getLoadedStems(): readonly LoadedStem[] {
    const out: LoadedStem[] = [];
    for (const chain of this.stems.values()) {
      out.push({ role: chain.role, url: "", buffer: chain.buffer });
    }
    return out;
  }

  setStemSettings(role: StemRole, settings: Partial<StemMixSettings>): void {
    const chain = this.stems.get(role);
    if (!chain) return;
    const next: StemMixSettings = {
      volume: settings.volume ?? chain.settings.volume,
      muted: settings.muted ?? chain.settings.muted,
      solo: settings.solo ?? chain.settings.solo,
    };
    chain.settings = next;
    this.recomputeStemGains();
    this.emit();
  }

  getStemSettings(role: StemRole): StemMixSettings | undefined {
    return this.stems.get(role)?.settings;
  }

  private recomputeStemGains(): void {
    const anySolo = [...this.stems.values()].some((c) => c.settings.solo);
    const now = this.ctx.currentTime;
    const ramp = this.volumeRampSec;
    for (const chain of this.stems.values()) {
      const audible = anySolo
        ? chain.settings.solo && !chain.settings.muted
        : !chain.settings.muted;
      const target = audible ? chain.settings.volume : 0;
      // cancelScheduledValues + setTargetAtTime gives a click-free ramp.
      chain.gain.gain.cancelScheduledValues(now);
      chain.gain.gain.setTargetAtTime(target, now, ramp);
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Transport
  // ──────────────────────────────────────────────────────────────────

  play(): void {
    if (this.stems.size === 0) return;
    if (this.playing) return;
    invariant(this.ctx.state === "running", "AudioContext must be running before play()");

    const startCtxTime = this.ctx.currentTime + 0.005;
    const offsetSec = this.pausePositionSec;
    for (const chain of this.stems.values()) {
      const src = this.ctx.createBufferSource();
      src.buffer = chain.buffer;
      src.playbackRate.value = this.playbackRate;
      src.connect(chain.gain);
      src.start(startCtxTime, offsetSec);
      chain.source = src;
    }
    this.startCtxTime = startCtxTime;
    this.startBufferOffsetSec = offsetSec;
    this.playing = true;
    this.setState("playing");
  }

  pause(): void {
    if (!this.playing) return;
    this.pausePositionSec = this.computePositionSec();
    this.stopSources();
    this.playing = false;
    this.setState("paused");
  }

  stop(): void {
    this.stopSources();
    this.playing = false;
    this.pausePositionSec = 0;
    if (this.stems.size > 0) {
      this.setState("ready");
    }
  }

  seek(positionSec: number): void {
    const clamped = Math.max(0, Math.min(this.duration, positionSec));
    const wasPlaying = this.playing;
    this.stopSources();
    this.pausePositionSec = clamped;
    this.playing = false;
    if (wasPlaying) {
      this.play();
    } else {
      this.emit();
    }
  }

  toggle(): void {
    if (this.playing) {
      this.pause();
    } else {
      this.play();
    }
  }

  private stopSources(): void {
    for (const chain of this.stems.values()) {
      const src = chain.source;
      if (src) {
        try {
          src.stop();
        } catch {
          // already-stopped sources throw; ignore.
        }
        src.disconnect();
        chain.source = null;
      }
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Metering
  // ──────────────────────────────────────────────────────────────────

  /**
   * Read the current RMS level (0..1, post-gain) for a given stem.
   *
   * Returns 0 if the stem isn't loaded, isn't playing, or the analyser hasn't
   * been attached yet. Allocates the analyser on first call per stem; the
   * sample buffer is reused thereafter so the hot path is allocation-free.
   *
   * Cheap to call at rAF rate (≈ 60 Hz × N stems).
   */
  getStemLevel(role: StemRole): number {
    const chain = this.stems.get(role);
    if (!chain) return 0;
    if (!this.playing) return 0;
    let analyser = chain.analyser;
    let buffer = chain.analyserBuffer;
    if (!analyser) {
      analyser = this.ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0;
      chain.gain.connect(analyser);
      analyser.connect(this.meterSink);
      chain.analyser = analyser;
    }
    if (!buffer) {
      buffer = new Float32Array(analyser.fftSize);
      chain.analyserBuffer = buffer;
    }
    analyser.getFloatTimeDomainData(buffer);
    let sumSq = 0;
    for (const v of buffer) {
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / buffer.length);
    // Clamp to [0,1]; `rms` rarely exceeds 1 unless mastering is hot.
    return rms > 1 ? 1 : rms;
  }

  // ──────────────────────────────────────────────────────────────────
  // Master controls
  // ──────────────────────────────────────────────────────────────────

  setMasterVolume(value: number): void {
    const v = Math.max(0, Math.min(1, value));
    const now = this.ctx.currentTime;
    this.masterGain.gain.cancelScheduledValues(now);
    this.masterGain.gain.setTargetAtTime(v, now, this.volumeRampSec);
    this.emit();
  }

  setPlaybackRate(rate: number): void {
    const r = Math.max(0.5, Math.min(2.0, rate));
    this.playbackRate = r;
    this.applyMasterStretch();
    if (this.playing) {
      // rebase: position-at-now becomes the new pause position; restart with new rate.
      const now = this.computePositionSec();
      this.stopSources();
      this.pausePositionSec = now;
      this.playing = false;
      this.play();
    } else {
      this.emit();
    }
  }

  /**
   * Set master pitch in semitones. When the player has a Signalsmith Stretch
   * adapter, pitch is decoupled from rate and applied on the audio thread.
   * Without the adapter, the value is stored and exposed via snapshots so the
   * UI can render it, but no DSP is performed.
   */
  setPitchSemitones(semitones: number): void {
    this.pitchSemitones = Math.max(-12, Math.min(12, semitones));
    this.applyMasterStretch();
    this.emit();
  }

  private applyMasterStretch(): void {
    if (!this.stretch) return;
    this.stretch.apply({
      pitchSemitones: this.pitchSemitones,
      playbackRate: this.playbackRate,
    });
  }

  // ──────────────────────────────────────────────────────────────────
  // Position / snapshot
  // ──────────────────────────────────────────────────────────────────

  getPositionSec(): number {
    return this.computePositionSec();
  }

  getDurationSec(): number {
    return this.duration;
  }

  snapshot(): PlayerSnapshot {
    return {
      state: this.state,
      positionSec: this.computePositionSec(),
      durationSec: this.duration,
      playbackRate: this.playbackRate,
      pitchSemitones: this.pitchSemitones,
      masterVolume: this.masterGain.gain.value,
    };
  }

  subscribe(listener: StemPlayerListener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  private computePositionSec(): number {
    if (!this.playing) return this.pausePositionSec;
    const elapsed = (this.ctx.currentTime - this.startCtxTime) * this.playbackRate;
    const pos = this.startBufferOffsetSec + Math.max(0, elapsed);
    if (pos >= this.duration && this.duration > 0) {
      // playback overran; clamp + stop deferred to next tick to avoid
      // re-entrant state changes.
      return this.duration;
    }
    return pos;
  }

  private setState(state: PlayerSnapshot["state"]): void {
    this.state = state;
    this.emit();
  }

  private emit(): void {
    if (this.listeners.size === 0) return;
    const snap = this.snapshot();
    for (const l of this.listeners) l(snap);
  }
}
